import { Avatar, AvatarInitials } from "@/components/Avatar";
import { Button } from "@/components/flexkit/Button";
import { Select } from "@/components/flexkit/select/Select";
import { Label } from "@/components/podkit/forms/Label";
import { Textarea } from "@/components/podkit/forms/Textarea";
import { SkeletonBlock } from "@/components/podkit/loading/Skeleton";
import { Switch } from "@/components/Switch";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useGetOrganizationPolicies, useUpdateOrganizationPolicies } from "@/queries/organization-queries";
import { useListGroups, type PlainGroup } from "@/queries/group-queries";
import { formatError } from "@/utils/errors";
import type { PartialMessage } from "@bufbuild/protobuf";
import type { OrganizationPolicies } from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useEffect, useMemo, useState, type FC } from "react";

const GroupOption: FC<{ group: PlainGroup }> = ({ group }) => (
    <span className="flex items-center gap-2">
        <Avatar size={24} className="size-5 rounded">
            <AvatarInitials name={group.name} size={24} />
        </Avatar>
        <span>{group.name}</span>
    </span>
);

const AllUsersOption: FC = () => (
    <span className="flex items-center gap-2">
        <span className="flex size-5 items-center justify-center rounded bg-surface-tertiary text-xs">∞</span>
        <span>All</span>
    </span>
);

// Special value for "All" option (no restriction)
const ALL_USERS_VALUE = "__all__";

export const AgentPolicies: FC = () => {
    const { data: organizationPolicies, isLoading } = useGetOrganizationPolicies();
    const { data: groupsData, isLoading: isLoadingGroups } = useListGroups();
    const updateOrganizationPolicies = useUpdateOrganizationPolicies();
    const { toast } = useToast();

    const [mcpEnabled, setMcpEnabled] = useState(true);
    const [scmToolsEnabled, setScmToolsEnabled] = useState(true);
    const [scmToolsAllowedGroupId, setScmToolsAllowedGroupId] = useState("");
    const [denyList, setDenyList] = useState("");
    const [isDenyListDirty, setIsDenyListDirty] = useState(false);

    // Find the admins group
    const adminsGroup = useMemo(() => {
        return groupsData?.groups.find((g) => g.name === "org-admins");
    }, [groupsData]);

    // Get custom groups (non-system-managed)
    const customGroups = useMemo(() => {
        return groupsData?.groups.filter((g) => !g.systemManaged) ?? [];
    }, [groupsData]);

    // Initialize state from API data
    useEffect(() => {
        if (organizationPolicies?.agentPolicy) {
            setMcpEnabled(!organizationPolicies.agentPolicy.mcpDisabled);
            setScmToolsEnabled(!organizationPolicies.agentPolicy.scmToolsDisabled);
            setScmToolsAllowedGroupId(organizationPolicies.agentPolicy.scmToolsAllowedGroupId || "");
            setDenyList(organizationPolicies.agentPolicy.commandDenyList.join("\n"));
        }
    }, [organizationPolicies]);

    const handleMcpToggle = useCallback(
        async (checked: boolean) => {
            const prevState = mcpEnabled;
            setMcpEnabled(checked);

            const params: PartialMessage<OrganizationPolicies> = {
                agentPolicy: {
                    mcpDisabled: !checked,
                    scmToolsDisabled: !scmToolsEnabled,
                    scmToolsAllowedGroupId: scmToolsAllowedGroupId,
                    commandDenyList: denyList.split("\n").filter((line) => line.trim()),
                },
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: checked ? "MCP enabled" : "MCP disabled",
                });
            } catch (error) {
                console.error(error);
                setMcpEnabled(prevState);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [mcpEnabled, scmToolsEnabled, scmToolsAllowedGroupId, denyList, updateOrganizationPolicies, toast],
    );

    const handleScmToolsToggle = useCallback(
        async (checked: boolean) => {
            const prevState = scmToolsEnabled;
            setScmToolsEnabled(checked);

            const params: PartialMessage<OrganizationPolicies> = {
                agentPolicy: {
                    mcpDisabled: !mcpEnabled,
                    scmToolsDisabled: !checked,
                    scmToolsAllowedGroupId: scmToolsAllowedGroupId,
                    commandDenyList: denyList.split("\n").filter((line) => line.trim()),
                },
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: checked ? "SCM tools enabled" : "SCM tools disabled",
                });
            } catch (error) {
                console.error(error);
                setScmToolsEnabled(prevState);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [mcpEnabled, scmToolsEnabled, scmToolsAllowedGroupId, denyList, updateOrganizationPolicies, toast],
    );

    const handleScmToolsAllowedGroupChange = useCallback(
        async (value: string) => {
            // Convert special "All" value back to empty string for API
            const groupId = value === ALL_USERS_VALUE ? "" : value;
            const prevState = scmToolsAllowedGroupId;
            setScmToolsAllowedGroupId(groupId);

            const params: PartialMessage<OrganizationPolicies> = {
                agentPolicy: {
                    mcpDisabled: !mcpEnabled,
                    scmToolsDisabled: !scmToolsEnabled,
                    scmToolsAllowedGroupId: groupId,
                    commandDenyList: denyList.split("\n").filter((line) => line.trim()),
                },
            };

            try {
                await updateOrganizationPolicies.mutateAsync(params);
                toast({
                    title: "SCM tools access updated",
                });
            } catch (error) {
                console.error(error);
                setScmToolsAllowedGroupId(prevState);
                toast({
                    title: "We couldn't save your changes. Sorry about that! Please retry.",
                    description: formatError(error),
                });
            }
        },
        [mcpEnabled, scmToolsEnabled, scmToolsAllowedGroupId, denyList, updateOrganizationPolicies, toast],
    );

    const handleDenyListChange = useCallback((value: string) => {
        setDenyList(value);
        setIsDenyListDirty(true);
    }, []);

    const handleSave = useCallback(async () => {
        const params: PartialMessage<OrganizationPolicies> = {
            agentPolicy: {
                mcpDisabled: !mcpEnabled,
                scmToolsDisabled: !scmToolsEnabled,
                scmToolsAllowedGroupId: scmToolsAllowedGroupId,
                commandDenyList: denyList.split("\n").filter((line) => line.trim()),
            },
        };

        try {
            await updateOrganizationPolicies.mutateAsync(params);
            setIsDenyListDirty(false);
            toast({
                title: "Command deny list updated",
            });
        } catch (error) {
            console.error(error);
            toast({
                title: "We couldn't save your changes. Sorry about that! Please retry.",
                description: formatError(error),
            });
        }
    }, [mcpEnabled, scmToolsEnabled, scmToolsAllowedGroupId, denyList, updateOrganizationPolicies, toast]);

    return (
        <div className="flex max-w-[455px] flex-col gap-4">
            <Heading3>Policies</Heading3>
            <SkeletonBlock ready={!isLoading}>
                <div className="flex flex-col gap-6" data-testid="agent-policies-section">
                    <Text className="text-base text-content-secondary">
                        Configure security policies and restrictions for AI agents in your organization.
                    </Text>

                    {/* MCP Toggle */}
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-4">
                            <div className="flex">
                                <Switch
                                    state={mcpEnabled ? "checked" : "unchecked"}
                                    onToggle={handleMcpToggle}
                                    id="mcp-toggle"
                                    aria-label="Enable Model Context Protocol (MCP)"
                                />
                            </div>
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-1">
                                    <Label className="text-base">Model Context Protocol (MCP)</Label>
                                    <Text className="text-sm text-content-secondary">
                                        Allow agents to use MCP servers for extended functionality
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* SCM Tools Toggle */}
                    <div className="flex flex-col gap-3">
                        <div className="flex gap-4">
                            <div className="flex">
                                <Switch
                                    state={
                                        updateOrganizationPolicies.isPending
                                            ? "pending"
                                            : scmToolsEnabled
                                              ? "checked"
                                              : "unchecked"
                                    }
                                    onToggle={handleScmToolsToggle}
                                    id="scm-tools-toggle"
                                    aria-label="Enable SCM Tools"
                                />
                            </div>
                            <div className="flex flex-col gap-3">
                                <div className="flex flex-col gap-1">
                                    <Label className="text-base">SCM Tools</Label>
                                    <Text className="text-sm text-content-secondary">
                                        Allow agents to interact with source control management systems (GitHub,
                                        GitLab, etc.)
                                    </Text>
                                </div>
                            </div>
                        </div>
                        {/* Group selector - only shown when SCM tools are enabled */}
                        {scmToolsEnabled && (
                            <div className="ml-12 flex flex-col gap-2">
                                <Label className="text-sm">Restrict access to</Label>
                                <Select
                                    value={scmToolsAllowedGroupId || ALL_USERS_VALUE}
                                    onValueChange={handleScmToolsAllowedGroupChange}
                                    disabled={isLoadingGroups || updateOrganizationPolicies.isPending}
                                    loading={isLoadingGroups}
                                    data-testid="scm-tools-group-select"
                                >
                                    <Select.Item value={ALL_USERS_VALUE}>
                                        <AllUsersOption />
                                    </Select.Item>
                                    {adminsGroup && (
                                        <Select.Item value={adminsGroup.id}>
                                            <GroupOption group={adminsGroup} />
                                        </Select.Item>
                                    )}
                                    {customGroups.map((group) => (
                                        <Select.Item key={group.id} value={group.id}>
                                            <GroupOption group={group} />
                                        </Select.Item>
                                    ))}
                                </Select>
                            </div>
                        )}
                    </div>

                    {/* Command Deny List */}
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-col gap-1">
                            <Label className="text-base font-medium">Command Deny List</Label>
                            <Text className="text-sm text-content-secondary">
                                Enter commands or patterns that should be blocked (one per line)
                            </Text>
                        </div>
                        <Textarea
                            value={denyList}
                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                                handleDenyListChange(e.target.value)
                            }
                            placeholder="rm -rf&#10;sudo&#10;curl&#10;wget"
                            minHeight={120}
                            maxHeight={400}
                            className="resize-none font-mono text-sm"
                            data-testid="deny-list-textarea"
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-2">
                        <Button
                            onClick={handleSave}
                            variant="primary"
                            disabled={!isDenyListDirty}
                            loading={updateOrganizationPolicies.isPending}
                            data-testid="save-policies-button"
                            data-tracking-id="save-changes-agent-policies"
                        >
                            Save Changes
                        </Button>
                    </div>
                </div>
            </SkeletonBlock>
        </div>
    );
};
